import { useEffect, useRef } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { toast } from 'react-toastify';
import { NetworkState } from 'src/constants/network-state';
import { IUserSlice } from 'src/redux/user/userSlice.contracts';
import { User } from 'switch-shared/modules/crud/user/user.entity';

import { AuthContextInterface } from '../context/AuthContextInterface';
import { clearSwitchUserCache } from '../utils/switchUserCache';

type LogoutReason = 'revoked-console-access';

export interface UseAuthBootstrapOptions {
  getTokens: AuthContextInterface['getTokens'];
  hasAuthenticationError: boolean;
  isSwitchUserError: boolean;
  isSwitchUserLoading: boolean;
  loading: boolean;
  logout: AuthContextInterface['logout'];
  navigate: NavigateFunction;
  onAuthenticated: (userAuth: IUserSlice) => void;
  switchUser: User | null | undefined;
  user: AuthContextInterface['user'];
}

/** Retrieves tokens and persists authenticated state after Firebase and Switch users load, redirecting or logging out when bootstrap fails. */
export const useAuthBootstrap = ({
  getTokens,
  hasAuthenticationError,
  isSwitchUserError,
  isSwitchUserLoading,
  loading,
  logout,
  navigate,
  onAuthenticated,
  switchUser,
  user,
}: UseAuthBootstrapOptions) => {
  const logoutReason = useRef<LogoutReason | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    const prepareAuthentication = async () => {
      try {
        const tokens = await getTokens();
        if (cancelled) return;

        onAuthenticated({
          switchUser: switchUser!,
          user,
          status: NetworkState.AUTHENTICATED,
          accessToken: tokens?.accessToken ?? null,
          refreshToken: tokens?.refreshToken ?? null,
          error: null,
        });
      } catch (error) {
        if (cancelled) return;

        console.log(error);
        toast.error(
          'There has been an error getting authentication details. Please try logging in'
        );
        navigate('/auth/login');
      }
    };

    if (!loading && !isSwitchUserLoading) {
      if (user && switchUser) {
        if (switchUser.revokeConsoleAccess) {
          logoutReason.current = 'revoked-console-access';
          void logout();
        } else {
          void prepareAuthentication();
        }
      } else {
        navigate('/auth/login');
        if (logoutReason.current === 'revoked-console-access') {
          toast.error('You are not authorized to access the console.');
        }
        logoutReason.current = undefined;
      }
    }

    if (hasAuthenticationError) {
      void logout();
    }

    if (isSwitchUserError) {
      clearSwitchUserCache();
      void logout();
    }

    return () => {
      cancelled = true;
    };
  }, [
    getTokens,
    hasAuthenticationError,
    isSwitchUserError,
    isSwitchUserLoading,
    loading,
    logout,
    navigate,
    onAuthenticated,
    switchUser,
    user,
  ]);
};
